import React, { useCallback, useMemo } from 'react';
// eslint-disable-next-line no-restricted-imports
import { faExclamationCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Alert from 'fe-design-base/molecules/Alert';
import Button from 'fe-design-base/molecules/Button';
import FDBDatePickerField from 'fe-design-base/organisms/FDBDatePickerField';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';

import Box from 'components/Box';
import Text from 'components/Text';

import { cxHelpers } from 'util/className';
import { datePickerFormat, df, formatDate, today } from 'util/dateTime';
import { toI18n } from 'util/i18n';

import FieldRenderer from '../../FieldRenderer';

import { HSA_LIMIT_OPTIONS } from './constants.js';
import PercentToolTip from './PercentToolTip';
import { useAdditionalBenefits } from './useAdditionalBenefits';

const { cxEl } = cxHelpers('BenefitFormFields');

const RADIO_GROUP_STYLE = {
  marginLeft: 0,
  marginRight: 8,
};

const BENEFIT_TYPES_401K = ['401k', 'roth_401k'];

const BENEFIT_TYPES_HEALTH_INSURANCE = [
  'fsa_medical',
  'fsa_dependent_care',
  'hsa',
  '125_vision',
  '125_dental',
  '125_medical',
  '125_medical_other',
  '125_hospital',
];

const BenefitFormFields = ({
  values,
  isGmod,
  initialValues,
  onClickRemove,
  canDelete = false,
  isRemoveButtonLoading = false,
  showFormWithSteps,
  isGuideline401kConfigured,
  isSimplyInsuredConfigured,
}) => {
  const { benefitTypeOptions, benefitTypeMap } = useAdditionalBenefits();

  const isNewBenefit = useMemo(
    () => !initialValues.benefit_id,
    [initialValues.benefit_id]
  );

  const contributionGroupOptions = useMemo(
    () => [
      {
        value: 'amount',
        description: (
          <Text
            fs={showFormWithSteps ? 14 : 16}
            fw400
            lh={1.25}
            navy
            i18n="team.employee_profile.tax_deductions.fixed_amount_label"
          />
        ),
      },
      {
        value: 'percent',
        description: (
          <PercentToolTip
            content={toI18n(
              'team.employee_profile.tax_deductions.percent_tooltip_content'
            )}
            i18n="team.employee_profile.tax_deductions.percent_label"
            showFormWithSteps={showFormWithSteps}
          />
        ),
      },
    ],
    [showFormWithSteps]
  );

  const blockDatesBefore = useMemo(() => {
    if (!isGmod) {
      return today(df('parsable_reversed'));
    }
  }, [isGmod]);

  const { setFieldValue } = useFormikContext();

  const handleStartDateBlur = useCallback(() => {
    setFieldValue('start_date', formatDate(values.start_date, 'parsable_date'));
  }, [setFieldValue, values.start_date]);

  const handleEndDateBlur = useCallback(() => {
    setFieldValue('end_date', formatDate(values.end_date, 'parsable_date'));
  }, [setFieldValue, values.end_date]);

  const is401kField = useMemo(
    () =>
      isGuideline401kConfigured &&
      isNewBenefit &&
      BENEFIT_TYPES_401K.includes(values.benefit_type),
    [isGuideline401kConfigured, values.benefit_type, isNewBenefit]
  );

  const isHealthInsuranceField = useMemo(
    () =>
      isSimplyInsuredConfigured &&
      isNewBenefit &&
      BENEFIT_TYPES_HEALTH_INSURANCE.includes(values.benefit_type),
    [isSimplyInsuredConfigured, values.benefit_type, isNewBenefit]
  );

  const isBlockedField = is401kField || isHealthInsuranceField;

  return (
    <Box pb20 bb mb20>
      <Box row mb32>
        <Box grow basis={0}>
          <Box mb12>
            <Text
              fs={showFormWithSteps ? 14 : 16}
              fw600
              lh={1.25}
              navy
              i18n="team.employee_profile.tax_deductions.type_label"
            />
            :
          </Box>
          <Box w={242}>
            <FieldRenderer
              className={cxEl('benefit_type')}
              editMode={!initialValues.benefit_type}
              name="benefit_type"
              component="SelectInput"
              options={benefitTypeOptions}
              hideClear
              componentProps={{ dataTestId: 'benefit-type-select' }}
              savedValue={
                initialValues.benefit_type
                  ? benefitTypeOptions.find(
                      option => option.value === initialValues.benefit_type
                    )?.label
                  : null
              }
            />
          </Box>
        </Box>

        {!isBlockedField && (
          <Box grow basis={150}>
            <Box mb12>
              <Text
                fs={showFormWithSteps ? 14 : 16}
                fw600
                lh={1.25}
                navy
                i18n="team.employee_profile.tax_deductions.name_label"
              />
              :
            </Box>
            <Box w={242}>
              <FieldRenderer
                editMode
                name="benefit_name"
                component="TextInput"
                componentProps={{ dataTestId: 'benefit-name-input' }}
              />
            </Box>
            <Text
              fs14
              fw400
              lh={1.25}
              navy
              i18n="team.employee_profile.tax_deductions.name_description_label"
            />
          </Box>
        )}
      </Box>
      {!isBlockedField && (
        <>
          <Box column mb32>
            {values.benefit_type === 'hsa' && (
              <Box grow basis={0} mb32>
                <Box mb12>
                  <Text
                    fs={showFormWithSteps ? 14 : 16}
                    fw600
                    lh={1.25}
                    navy
                    i18n="team.employee_profile.tax_deductions.hsa.title"
                  />
                  :
                </Box>
                <Box w={242}>
                  <FieldRenderer
                    className={cxEl('benefit_type')}
                    editMode
                    name="hsa_contribution_limit"
                    component="SelectInput"
                    options={HSA_LIMIT_OPTIONS}
                    hideClear
                  />
                </Box>
              </Box>
            )}
            {values.benefit_type && (
              <Box grow bradius bg="blueLightest" pa20>
                <Text
                  fs16
                  fw400
                  lh={1.25}
                  navy
                  i18n={benefitTypeMap[values.benefit_type].description}
                />
              </Box>
            )}
          </Box>
          <Box row mb32>
            <Box grow basis={0}>
              <Box mb12>
                <Text
                  fs={showFormWithSteps ? 14 : 16}
                  fw600
                  lh={1.25}
                  navy
                  i18n="team.employee_profile.tax_deductions.employee_contribution_label"
                />
                :
              </Box>
              <FieldRenderer
                editMode
                name="employee_contribution_type"
                component="RadioGroup"
                row
                options={contributionGroupOptions}
                radioStyle={RADIO_GROUP_STYLE}
                labelStyle={{
                  marginLeft: 0,
                  width: 160,
                }}
                inputProps={{ description: 'other' }}
              />
              <Box row>
                <FieldRenderer
                  editMode
                  name="employee_contribution"
                  component="TextInput"
                  componentProps={{ dataTestId: 'employee-contribution-input' }}
                  inputProps={{
                    decimalScale: 2,
                  }}
                  placeholder={
                    values.employee_contribution_type === 'amount'
                      ? '$0.00'
                      : '0.00%'
                  }
                  type={
                    values.employee_contribution_type === 'amount'
                      ? 'currency'
                      : 'percent'
                  }
                />
                <Box vcenter ml12>
                  <Text
                    fs={showFormWithSteps ? 14 : 16}
                    fw600
                    lh={1.25}
                    navy
                    i18n="team.employee_profile.tax_deductions.per_pay_period_label"
                  />
                </Box>
              </Box>
            </Box>

            <Box grow basis={150}>
              <Box mb12>
                <Text
                  fs={showFormWithSteps ? 14 : 16}
                  fw600
                  lh={1.25}
                  navy
                  i18n="team.employee_profile.tax_deductions.company_contribution_label"
                />
                :
              </Box>
              <FieldRenderer
                editMode
                name="company_contribution_type"
                component="RadioGroup"
                row
                options={contributionGroupOptions}
                radioStyle={RADIO_GROUP_STYLE}
                labelStyle={{ width: 160 }}
              />
              <Box row>
                <FieldRenderer
                  editMode
                  name="company_contribution"
                  componentProps={{ dataTestId: 'company-contribution-input' }}
                  component="TextInput"
                  inputProps={{
                    decimalScale: 2,
                  }}
                  placeholder={
                    values.company_contribution_type === 'amount'
                      ? '$0.00'
                      : '0.00%'
                  }
                  type={
                    values.company_contribution_type === 'amount'
                      ? 'currency'
                      : 'percent'
                  }
                />
                <Box vcenter ml12>
                  <Text
                    fs={showFormWithSteps ? 14 : 16}
                    fw600
                    lh={1.25}
                    navy
                    i18n="team.employee_profile.tax_deductions.per_pay_period_label"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
          <Box row mb32>
            <Box grow={2} basis={0}>
              <Box mb12>
                <Text
                  fs={showFormWithSteps ? 14 : 16}
                  fw600
                  lh={1.25}
                  navy
                  i18n="team.employee_profile.tax_deductions.start_date_label"
                />
                :
              </Box>
              <Box maxw={220} className={cxEl('start-date')}>
                <FDBDatePickerField
                  hideClearIcon
                  name="start_date"
                  blockDatesBefore={blockDatesBefore}
                  value={
                    values.start_date
                      ? datePickerFormat(values.start_date)
                      : null
                  }
                  onBlur={handleStartDateBlur}
                  dataTestId="benefit-start-date-picker"
                />
              </Box>
            </Box>
            <Box grow={1} basis={150}>
              <Box mb12>
                <Text
                  fs={showFormWithSteps ? 14 : 16}
                  fw600
                  lh={1.25}
                  navy
                  i18n="team.employee_profile.tax_deductions.end_date_optional_label"
                />
                :
              </Box>
              <Box maxw={220} className={cxEl('end-date')}>
                <FDBDatePickerField
                  hideClearIcon
                  name="end_date"
                  blockDatesBefore={values.start_date}
                  value={
                    values.end_date ? datePickerFormat(values.end_date) : null
                  }
                  onBlur={handleEndDateBlur}
                />
              </Box>
            </Box>
            <Box vbottom hright grow={1} basis={0}>
              {canDelete && (
                <Box className={cxEl('remove-benefit-button')}>
                  <Button
                    size={showFormWithSteps ? 'small' : 'medium'}
                    variant="secondaryDestructive"
                    onClick={onClickRemove}
                    isLoading={isRemoveButtonLoading}
                  >
                    {toI18n(
                      'team.employee_profile.tax_deductions.remove_benefit'
                    )}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
          <Box row mb12>
            <Box row grow basis={0} bradius bg="grayLight" pa20 vcenter>
              <FontAwesomeIcon icon={faExclamationCircle} size="lg" />
              <Text
                ml8
                fs16
                fw400
                lh={1.25}
                navy
                i18n="team.employee_profile.tax_deductions.reminder"
                asHTML
              />
            </Box>
          </Box>
        </>
      )}
      {isBlockedField && (
        <Alert
          variant="info"
          mb={20}
          startIcon={<FontAwesomeIcon icon={faExclamationCircle} />}
        >
          {is401kField && (
            <Text i18n="features.benefits.retirement.team_profile.alert_401_configured_blocked_manual_setup.title" />
          )}
          {isHealthInsuranceField && (
            <Text i18n="features.benefits.health.team_profile.alert_si_configured.title" />
          )}
        </Alert>
      )}
    </Box>
  );
};

BenefitFormFields.propTypes = {
  values: PropTypes.object,
  onClickRemove: PropTypes.func,
  canDelete: PropTypes.bool,
};

export default BenefitFormFields;
